import { Carousel } from '@mantine/carousel';
import {
  ActionIcon,
  Box,
  Button,
  Container,
  Flex,
  Grid,
  HoverCard,
  Image,
  Menu,
  Modal,
  Paper,
  Rating,
  Text,
  createStyles,
  useMantineTheme,
  Select,
  Transition,
  Badge
} from '@mantine/core';
import {
  IconArrowsMaximize,
  IconHeart,
  IconMoneybag,
  IconPercentage,
  IconPlus,
  IconStar,
  IconStars,
  IconTrendingUp,
  IconShare,
  IconClock,
  IconInfoCircle
} from '@tabler/icons-react';
import { image_url, is_mobile_view } from '../config';
import { useNavigate } from 'react-router';
import { useMediaQuery } from '@mantine/hooks';
import { IconShoppingCart } from '@tabler/icons';
import { useRef, useMemo, useState, useEffect } from 'react';
import { priceCalculator } from '../utilities/Common';
import { api } from '../services/master.service';
import { showErrorToast, showSuccessToast } from '../utilities/Toast';

const useStyles = createStyles((theme) => ({
  card: {
    position: 'relative',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)',
    border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]}`,
    borderRadius: theme.radius.md,
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
    overflow: 'hidden',
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: theme.shadows.xl,
      borderColor: theme.colors.brand[5],
    },
  },

  imageSection: {
    position: 'relative',
    padding: theme.spacing.sm,
    backgroundColor: theme.white,
    height: 150,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    boxShadow: 'inset 0 -6px 10px -4px rgba(0,0,0,0.03)',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: 16,
      background: 'linear-gradient(to top, rgba(0,0,0,0.03), transparent)',
      pointerEvents: 'none',
    },
  },

  image: {
    transition: 'transform 0.6s ease',
    '&:hover': {
      transform: 'scale(1.1)',
    },
  },

  content: {
    padding: theme.spacing.sm,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },

  title: {
    height: 36, // Fixed height for 2 lines
    lineHeight: '18px',
    fontWeight: 600,
    marginBottom: 4,
  },

  statusArea: {
    height: 18,
    marginBottom: 4,
  },

  priceArea: {
    marginTop: 'auto',
    paddingTop: theme.spacing.sm,
  },

  badge: {
    position: 'absolute',
    top: 10,
    right: 10,
    zIndex: 2,
    backdropFilter: 'blur(4px)',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },

  bestBeforeBadge: {
    position: 'absolute',
    top: 10,
    left: 10,
    zIndex: 2,
    padding: '4px 8px',
    borderRadius: theme.radius.sm,
    fontSize: 10,
    fontWeight: 700,
    color: theme.white,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  }
}));

export default function ProductCard(props) {
  const { classes } = useStyles();
  let isMobileScreen = useMediaQuery('(max-width: 768px)');
  isMobileScreen = is_mobile_view ? is_mobile_view : isMobileScreen;
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const cartRef = useRef();

  const getInitialVariant = (product) => {
    if (product?.isVarient && Array.isArray(product?.variants) && product.variants.length > 0) {
      const firstVariant = Number(product.variants[0]);
      return isNaN(firstVariant) ? 1 : firstVariant;
    }
    return 1;
  };

  const [selectedValue, setSelectedValue] = useState(() => getInitialVariant(props?.object));
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const newValue = getInitialVariant(props?.object);
    setSelectedValue((prev) => (prev !== newValue ? newValue : prev));
  }, [props?.object]);

  const varients = useMemo(() => {
    const product = props?.object;
    if (product?.isVarient && Array.isArray(product?.variants) && product.variants.length > 0) {
      const sortedData = [...product.variants].sort((a, b) => Number(a) - Number(b));
      return sortedData.map((value) => ({
        label: product.varientUnit == 'kg' && value < 1 ? `${value * 1000}${'g'}` : `${value}${product.varientUnit ?? ''}`,
        value: Number(value),
      }));
    }
    return [{ label: '1', value: 1 }];
  }, [props?.object]);

  const outOfStock = props?.object?.stock_qty <= props?.object?.min_qty || props?.object?.stock_qty <= 0 || !props?.object?.is_batch;

  const batchPrices = useMemo(() => {
    if (props.object?.active_batches && props.object.active_batches.length > 0) {
      return props.object.active_batches.map(b => Number(b.selling_price));
    }
    return [Number(props.object.price)];
  }, [props.object]);

  const minPrice = Math.min(...batchPrices);
  const maxPrice = Math.max(...batchPrices);
  const hasPriceRange = minPrice !== maxPrice;
  const currentPrice = props.object?.active_batches?.[0]?.selling_price ?? props.object.price;
  const currentMrp = props.object?.active_batches?.[0]?.mrp ?? props.object.mrp;

  const getFreshDays = () => {
    const expDate = props?.object?.batch_exp_date;
    if (!expDate) return null;
    const today = new Date();
    const expiry = new Date(expDate);
    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const freshDays = getFreshDays();

  const getStatusColor = (days) => {
    if (days <= 3) return theme.colors.red[6];
    if (days <= 7) return theme.colors.orange[6];
    return theme.colors.teal[6];
  };

  return (
    <div style={{ height: '100%' }}>
      <Paper
        className={classes.card}
        shadow="xs"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Top Section with Image and Badges */}
        <Box className={classes.imageSection} onClick={() => navigate('/product' + `/?id=${props.object.id}`)}>
          {freshDays !== null && !outOfStock && (
            <div
              className={classes.bestBeforeBadge}
              style={{ backgroundColor: getStatusColor(freshDays) }}
            >
              <IconClock size={12} />
              <span>{freshDays}d Left</span>
            </div>
          )}

          {outOfStock && (
            <Badge variant="outline" color="red" className={classes.badge} sx={{ top: 10, right: 10 }}>
              Sold Out
            </Badge>
          )}

          <Image
            src={image_url + props.object.thumbnail}
            width={'100%'}
            height={isMobileScreen ? 100 : 130}
            fit="contain"
            className={classes.image}
          />
        </Box>

        {/* Content Section */}
        <Box className={classes.content}>
          <Text className={classes.title} lineClamp={2} size="sm">
            {props.object.title}
          </Text>

          <Box className={classes.statusArea}>
            {!isMobileScreen && (
              <Flex align="center" gap={4}>
                <Rating readOnly value={props.object.review} size="xs" />
                <Text size="xs" color="dimmed">({props.object.review})</Text>
              </Flex>
            )}
          </Box>

          <Flex align="center" gap={10} mb={10}>
            <Select
              data={varients}
              size="xs"
              style={{ flex: 1 }}
              disabled={!props?.object?.isVarient}
              value={selectedValue}
              onChange={setSelectedValue}
              variant="filled"
              radius="md"
            />
          </Flex>

          <Box className={classes.priceArea}>
            <Flex align="baseline" gap={8} mb={4}>
              <Text fw={700} size="xl" color="brand.6">
                ₹{priceCalculator(currentPrice, selectedValue, props?.object?.varientUnit, props?.object?.isVarient, 1)}
              </Text>
              <Text strikethrough size="xs" color="red.6">
                ₹{priceCalculator(currentMrp, selectedValue, props?.object?.varientUnit, props?.object?.isVarient, 1)}
              </Text>
            </Flex>

            <div style={{ height: 14 }}>
              {hasPriceRange && (
                <Flex align="center" gap={4} mt={2}>
                  <IconInfoCircle size={10} color={theme.colors.blue[6]} />
                  <Text size={10} color="blue.6" fw={600} sx={{ letterSpacing: '0.2px' }}>
                    Price varies by batch
                  </Text>
                </Flex>
              )}
            </div>
          </Box>

          <Box mt={15}>
            {outOfStock ? (
              <Button
                fullWidth
                radius="md"
                variant="light"
                color="orange"
                leftIcon={<IconPlus size={16} />}
                onClick={async (e) => {
                  e.stopPropagation();
                  const res = await api.notify_me({
                    product_id: props.object.id,
                    status: "Notified",
                    add_cart: false,
                  });
                  if (res.success) {
                    showSuccessToast({ title: "Success", message: "We'll notify you!" });
                  } else {
                    showErrorToast({ title: "Note", message: res.message });
                  }
                }}
              >
                Notify Me
              </Button>
            ) : (
              <Button
                fullWidth
                radius="md"
                variant="filled"
                color="brand"
                leftIcon={<IconPlus size={16} />}
                disabled={props.object.stock_qty <= props.object.min_qty}
                onClick={(e) => {
                  e.stopPropagation();
                  props.addToCart(
                    props.object.id,
                    selectedValue,
                    props?.object?.varientUnit,
                    props?.object?.isVarient
                  );
                }}
                sx={{
                  transition: 'transform 0.2s ease',
                  '&:active': { transform: 'scale(0.95)' }
                }}
              >
                Add to Cart
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </div>
  );
}
